import { createRoot } from "react-dom/client";
import { useEffect, useState } from "react";
import emotionNormalize from "emotion-normalize";

import type { ScopedTalknClient } from "@api-client/TalknAPI";
import "@api-client/index";
import type ApiState from "@api-client/state";
import App from "./App";
import { registerApp, getHtmlDataOption } from "../../../index";
import type { HtmlDataOption, TalknWidgetRenderArgs } from "../../../index";

// Note: TalknAPI type is defined in @api-client/index
// Avoid redeclaring it to prevent type conflicts

import { parseMaxItems } from "../../../src/utils/parseMaxItems";
import { parseShowEmpty } from "../../../src/utils/parseShowEmpty";

registerApp("talkn-card", async ({ rootElm, connection, rankScope, tuneOption, connectionMode }: TalknWidgetRenderArgs) => {
  const talknAPI = await window.TalknAPI(connection, { tuneOption, rankScope, connectionMode });
  const htmlDataOption = getHtmlDataOption(rootElm);
  const maxItems = parseMaxItems(rootElm);
  const showEmpty = parseShowEmpty(rootElm);
  const reactRoot = createRoot(rootElm, { identifierPrefix: rootElm.id });
  reactRoot.render(
    <TalknThreadContainer
      talknAPI={talknAPI}
      root={rootElm}
      htmlDataOption={htmlDataOption}
      maxItems={maxItems}
      showEmpty={showEmpty}
    />
  );
});

declare global {
  namespace NodeJS {
    interface ProcessEnv {
      GOOGLE_CLIENT_ID: string;
      GOOGLE_CLIENT_SECRET: string;
    }
  }
}

export type Props = {
  talknAPI: ScopedTalknClient;
  state: ApiState;
  root: Element;
  htmlDataOption: HtmlDataOption;
  maxItems: number;
  showEmpty: boolean;
};

type TalknThreadContainerProps = {
  talknAPI: ScopedTalknClient;
  root: HTMLElement;
  htmlDataOption: HtmlDataOption;
  maxItems: number;
  showEmpty: boolean;
};

const TalknThreadContainer = ({
  talknAPI,
  root,
  htmlDataOption,
  maxItems,
  showEmpty,
}: TalknThreadContainerProps) => {
  const [state, setState] = useState<ApiState>(() => talknAPI.getState());

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    unsubscribe = talknAPI.subscribe((nextState) => {
      setState({ ...nextState });
    });

    const initialState = talknAPI.getState();

    // 新しいオブジェクトを作成してReactに変更を検知させる
    setState({ ...initialState });

    // Fetch rank data if not available
    const currentState = talknAPI.getState();
    if (currentState.rank.length === 0) {
      talknAPI.fetchRank().catch((error) => {
        console.error("Failed to fetch rank:", error);
      });
    }

    return () => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [talknAPI]);

  return (
    <>
      <style type="text/css">{emotionNormalize.styles}</style>
      <App
        talknAPI={talknAPI}
        state={state}
        root={root}
        htmlDataOption={htmlDataOption}
        maxItems={maxItems}
        showEmpty={showEmpty}
      />
    </>
  );
};

// Note: Auto-initialization is handled by widgets/src/entries/*.ts
// Do not add window.addEventListener('load') here to avoid duplicate rendering

export default TalknThreadContainer;
